import React from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { FaArrowAltCircleRight, FaFileDownload } from 'react-icons/fa';
import './import-progress.css';

import { FieldSelectCollector } from 'shared-pages/components';
import { ButtonCloseModal } from 'shared-pages/components';
import { ImportFieldHelp } from './ImportFieldHelp';

interface PreImportModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (values: { collectorId: number }) => void;
  onDownloadTemplate?: () => void;
}

type ImportFormValues = {
  collectorId: number | '';
};

export const PreImportModal: React.FC<PreImportModalProps> = ({
  show,
  onClose,
  onSubmit,
  onDownloadTemplate,
}) => {
  const { t } = useTranslation();

  const validationSchema = Yup.object().shape({
    collectorId: Yup.number().required(t('Απαιτείται Συλλέκτης')),
  });

  return (
    <Modal show={show} onHide={onClose} centered size="xl" backdrop="static" className="ip-modal">
      <Formik<ImportFormValues>
        initialValues={{
          collectorId: '',
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          onSubmit({
            collectorId: Number(values.collectorId),
          });
        }}
      >
        {({ handleSubmit, isSubmitting, values }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Modal.Header className="ip-modal-header">
              <Modal.Title className="ip-modal-title">
                {t('Εισαγωγή Συλλογών από Excel')}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body
              style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '0 0 var(--ip-border-radius) var(--ip-border-radius)',
                padding: 'var(--ip-spacing-lg)',
                fontSize: '0.9rem',
              }}
            >
              <div className="d-flex justify-content-between align-items-start mb-3">
                <p className="text-muted small mb-0">
                  {t(
                    'Επιλέξτε συλλέκτη πριν ξεκινήσει η εισαγωγή. Το σημείο παραλαβής διαβάζεται από το Excel. Μετά την ολοκλήρωση θα λάβετε ενημερωμένο αρχείο με το αποτέλεσμα ανά γραμμή.',
                  )}
                </p>
                {onDownloadTemplate && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="text-nowrap ms-3 flex-shrink-0"
                    onClick={onDownloadTemplate}
                    type="button"
                  >
                    <FaFileDownload className="mb-1 me-1" />
                    {t('Λήψη προτύπου')}
                  </Button>
                )}
              </div>

              <div className="mb-4">
                <FieldSelectCollector name="collectorId" label={t('Συλλέκτης')} required />
              </div>

              <ImportFieldHelp />
            </Modal.Body>

            <Modal.Footer
              className="ip-modal-footer"
              style={{
                borderTop: '1px solid var(--ip-border-color)',
                paddingTop: 'var(--ip-spacing-md)',
                backgroundColor: '#fff',
              }}
            >
              <div className="d-flex justify-content-between w-100 align-items-center">
                <ButtonCloseModal onClick={onClose} />

                <Button
                  variant="info"
                  type="submit"
                  disabled={!values.collectorId || isSubmitting}
                  className="px-4 shadow-sm"
                  style={{
                    backgroundColor: 'var(--ip-success)',
                    border: 'none',
                    color: '#fff',
                    borderRadius: 'var(--ip-border-radius)',
                    fontWeight: 600,
                    letterSpacing: '0.3px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                  }}
                >
                  <FaArrowAltCircleRight className="mb-1" />
                  &nbsp;
                  {t('Έναρξη εισαγωγής')}
                </Button>
              </div>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};
